import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import Link from '../../Link';
import {
  saveInspectionDraft, validateInspection, submitCheckout, writeInspectionReview
} from '../../../actions/Inspection/inspection';
import {
  formFromInspection, validateForm, validateReview, buildInspectionInput, photoIdsOf, isVersionConflict, fuelDelta, FORM_LIMITS
} from '../../../helpers/inspectionForm';
import history from '../../../core/history';
import useInspectionData from '../useInspectionData';
import usePhotoSeries from '../usePhotoSeries';
import InspectionShell from '../InspectionShell';
import InspectionBanner from '../InspectionBanner/InspectionBanner';
import InspectionPhotoGrid from '../InspectionPhotoGrid/InspectionPhotoGrid';
import MileageInput from '../MileageInput/MileageInput';
import FuelLevelSelector from '../FuelLevelSelector/FuelLevelSelector';
import InspectionDetails from '../InspectionDetails/InspectionDetails';
import InspectionStatusBadge from '../InspectionStatusBadge/InspectionStatusBadge';
import InspectionReview from '../InspectionReview/InspectionReview';
import DisputePanel from '../DisputePanel/DisputePanel';
import s from '../Inspection.css';

const TYPE = 'checkout';
const REVIEW_OK = '200';

// Check-out (R7 to R11, R22). The renter answers Yes / No; Yes only asks mileage, fuel, keys and a comment (no new photos),
// No opens the damage declaration (damage photos only, 8 max). The owner validates, or edits to declare a damage.
// A dispute (R12) is a separate step: "Report a disagreement". The review goes through the existing module in a separate
// call whose failure never blocks the check-out.
export function CheckoutPage({ reservationId }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { state, reload, setState } = useInspectionData({ reservationId, type: TYPE });
  const [form, setForm] = useState(formFromInspection(null, TYPE));
  const [started, setStarted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [errors, setErrors] = useState({});
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const versionRef = useRef(null);
  const seeded = useRef(null);

  const ready = state.kind === 'ready';
  const inspection = ready ? state.inspection : null;
  const checkin = ready ? state.checkin : null;
  const dispute = ready ? state.dispute : null;
  const role = ready ? state.result.viewerRole : null;
  const reservation = ready ? state.reservation : null;
  const windowOpen = ready ? state.result.windowOpen : false;
  const disputeActive = Boolean(dispute && dispute.isActive);
  const disputeExists = Boolean(dispute);

  const onVersion = useCallback((version) => { versionRef.current = version; }, []);
  const series = usePhotoSeries({
    reservationId, type: TYPE, kind: 'damage', initial: [], onVersion,
    ensureReady: async () => ensureDraft(),
    onError: (failure) => setNotice({ tone: 'danger', text: failure.serverMessage || formatMessage(messages[failure.messageKey]) })
  });
  const seriesRef = useRef(series);
  seriesRef.current = series;
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (state.kind !== 'ready') return;
    const key = `${state.inspection ? state.inspection.id : 'none'}:${state.inspection ? state.inspection.version : 0}`;
    if (seeded.current === key) return;
    seeded.current = key;
    versionRef.current = state.inspection ? state.inspection.version : null;
    setForm(formFromInspection(state.inspection, TYPE));
    seriesRef.current.reset(state.inspection ? state.inspection.photos : []);
  }, [state]);

  useEffect(() => {
    if (state.kind === 'notLoggedIn' && typeof window !== 'undefined') history.push('/login');
  }, [state.kind]);

  const mode = useMemo(() => {
    if (!ready) return 'loading';
    if (!inspection) return role === 'guest' && windowOpen ? (started ? 'form' : 'question') : 'empty';
    if (editing && !disputeExists) return 'form';
    if (inspection.status === 'draft') {
      if (!inspection.canEdit || disputeExists) return 'readonly';
      return form.hasDamage === null && !started ? 'question' : 'form';
    }
    if (inspection.status === 'to_validate' && inspection.canValidate && !disputeActive) return 'recap';
    return 'readonly';
  }, [ready, inspection, role, windowOpen, started, editing, disputeExists, disputeActive, form.hasDamage]);

  const photoCount = series.items.filter((item) => item.status !== 'failed').length;
  const isOwner = role === 'host';
  const otherId = reservation ? (isOwner ? reservation.guestId : reservation.hostId) : null;
  const reviewRequired = !isOwner && !inspection;

  const failureText = (result) => (result && result.errorMessage) || formatMessage(messages.inspErrGeneric);
  const applyResult = (result) => {
    if (result && result.inspection) {
      seeded.current = null;
      setState((current) => ({ ...current, result: { ...current.result, ...result }, inspection: result.inspection }));
      versionRef.current = result.inspection.version;
    }
  };
  // After a refused write: reload and tell the user when somebody else changed the record meanwhile.
  const handleFailure = async (result, displayedVersion) => {
    const reloaded = await reload();
    seeded.current = null;
    if (reloaded && reloaded.kind === 'ready' && isVersionConflict(displayedVersion, reloaded.inspection)) {
      setEditing(false);
      setNotice({ tone: 'warning', text: formatMessage(messages.inspVersionConflict) });
    } else setNotice({ tone: 'danger', text: failureText(result) });
  };

  // First damage photo of a new check-out: the draft must exist before the upload.
  async function ensureDraft() {
    if (versionRef.current !== null) return true;
    const input = buildInspectionInput({ ...formRef.current, hasDamage: true }, TYPE, { draft: true });
    const result = await dispatch(saveInspectionDraft({ reservationId, type: TYPE, input, expectedVersion: null }));
    if (result && result.status === 'success' && result.inspection) {
      versionRef.current = result.inspection.version;
      seeded.current = `${result.inspection.id}:${result.inspection.version}`;
      return true;
    }
    setNotice({ tone: 'danger', text: failureText(result) });
    return false;
  }

  const setField = (field, value) => { setForm((current) => ({ ...current, [field]: value })); setErrors((current) => ({ ...current, [field]: null })); };

  const checkErrors = ({ withReview }) => {
    const list = validateForm(formRef.current, { type: TYPE, photoCount, checkinMileage: checkin ? Number(checkin.mileage) : undefined });
    const map = {};
    list.forEach((item) => { map[item.field] = item.messageKey; });
    if (formRef.current.hasDamage === false && photoCount > 0) map.photos = 'inspErrRemoveDamagePhotos';
    if (withReview && (reviewRequired || rating > 0 || review.trim())) {
      const reviewError = validateReview({ rating, content: review });
      if (reviewError) map.review = reviewError;
    }
    setErrors(map);
    return Object.keys(map).length === 0;
  };

  // Separate call to the existing review module: null = nothing to send, true / false = result.
  const sendReview = async () => {
    if (!(rating > 0 && review.trim()) || !reservation) return null;
    const result = await dispatch(writeInspectionReview({
      reservationId, listId: reservation.listId, receiverId: otherId, reviewContent: review.trim(), rating
    }));
    return Boolean(result && String(result.status) === REVIEW_OK);
  };

  const withReviewNotice = async (successKey) => {
    const reviewed = await sendReview();
    if (reviewed === false) {
      setNotice({ tone: 'warning', text: formatMessage(messages.inspReviewFailed), link: `/review/write/${reservationId}` });
    } else setNotice({ tone: 'success', text: formatMessage(successKey) });
  };

  const submit = async () => {
    setNotice(null);
    if (!checkErrors({ withReview: true })) { setNotice({ tone: 'danger', text: formatMessage(messages.inspFixErrors) }); return; }
    setBusy(true);
    const displayed = versionRef.current;
    const hasDamage = Boolean(formRef.current.hasDamage);
    const input = {
      ...buildInspectionInput(formRef.current, TYPE),
      photoIds: hasDamage ? photoIdsOf(seriesRef.current.readyItems) : [],
      ...(displayed !== null && displayed !== undefined ? { expectedVersion: displayed } : {})
    };
    const result = await dispatch(submitCheckout({ reservationId, hasDamage, input }));
    if (result && result.status === 'success') {
      applyResult(result);
      setEditing(false);
      await withReviewNotice(messages.inspCheckoutSubmitted);
    } else await handleFailure(result, displayed);
    setBusy(false);
  };

  const validateRecap = async () => {
    setNotice(null);
    if (rating > 0 || review.trim()) {
      const reviewError = validateReview({ rating, content: review });
      if (reviewError) { setErrors({ review: reviewError }); return; }
    }
    setBusy(true);
    const displayed = inspection.version;
    const result = await dispatch(validateInspection({ reservationId, type: TYPE, version: displayed }));
    if (result && result.status === 'success') {
      applyResult(result);
      await withReviewNotice(result.inspection.status === 'validated' ? messages.inspCheckoutLocked : messages.inspCheckoutValidatedWaiting);
    } else await handleFailure(result, displayed);
    setBusy(false);
  };

  const startEdit = () => {
    setNotice(null);
    // The owner edits to declare a damage (R10): the answer is fixed to "damage".
    if (isOwner) setForm((current) => ({ ...current, hasDamage: true }));
    setEditing(true);
  };

  const answer = (hasDamage) => { setForm((current) => ({ ...current, hasDamage })); setStarted(true); setErrors({}); };

  const delta = checkin && form.fuelLevel ? fuelDelta(checkin.fuelLevel, form.fuelLevel) : null;
  const subtitle = (() => {
    if (mode === 'question') return formatMessage(messages.inspCheckoutSubtitleQuestion);
    if (mode === 'form') return formatMessage(form.hasDamage ? messages.inspCheckoutSubtitleDamage : messages.inspCheckoutSubtitleNoDamage);
    if (mode === 'recap') return formatMessage(messages.inspCheckoutSubtitleRecap);
    return null;
  })();
  const showPhotos = form.hasDamage === true || photoCount > 0;

  const reviewBlock = (
    <InspectionReview
      id="checkout-review"
      title={formatMessage(isOwner ? messages.inspReviewRenterTitle : messages.inspReviewOwnerTitle)}
      hint={formatMessage(isOwner ? messages.inspReviewRenterHint : messages.inspReviewOwnerHint)}
      rating={rating}
      content={review}
      onRating={(value) => { setRating(value); setErrors((current) => ({ ...current, review: null })); }}
      onContent={(value) => { setReview(value); setErrors((current) => ({ ...current, review: null })); }}
      required={reviewRequired}
      errorKey={errors.review}
    />
  );

  return (
    <InspectionShell title={formatMessage(messages.inspCheckoutTitle)} subtitle={subtitle} state={state} bannerWhenClosed>
      {notice && (
        <InspectionBanner tone={notice.tone}>
          {notice.text}
          {notice.link && <> <Link to={notice.link}>{formatMessage(messages.inspReviewWriteLink)}</Link></>}
        </InspectionBanner>
      )}

      {disputeActive && <InspectionBanner tone="danger" title={formatMessage(messages.inspDisputeOngoingTitle)}>{formatMessage(messages.inspDisputeOngoingText)}</InspectionBanner>}
      {dispute && !disputeActive && <InspectionBanner tone="success" title={formatMessage(messages.inspDisputeResolvedTitle)}>{formatMessage(messages.inspDisputeResolvedText)}</InspectionBanner>}

      {mode === 'empty' && role === 'host' && <InspectionBanner tone="info" title={formatMessage(messages.inspCheckoutWaitingRenter)} />}

      {mode === 'question' && (
        <section className={s.card}>
          <h2 className={s.cardTitle}>{formatMessage(messages.inspQuestionTitle)}</h2>
          <p className={s.cardHint}>{formatMessage(messages.inspQuestionHint)}</p>
          <div className={s.choiceRow}>
            <button type="button" className={cx(s.choice, s.choiceYes)} onClick={() => answer(false)}>
              <span aria-hidden="true">{'✓'}</span>
              <span className={s.choiceTitle}>{formatMessage(messages.inspYes)}</span>
              <span>{formatMessage(messages.inspQuestionYesHint)}</span>
            </button>
            <button type="button" className={cx(s.choice, s.choiceNo)} onClick={() => answer(true)}>
              <span aria-hidden="true">!</span>
              <span className={s.choiceTitle}>{formatMessage(messages.inspNo)}</span>
              <span>{formatMessage(messages.inspQuestionNoHint)}</span>
            </button>
          </div>
          <p className={s.infoStrip}>{formatMessage(messages.inspQuestionInfo)}</p>
        </section>
      )}

      {mode === 'form' && (
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          {form.hasDamage === false
            ? <InspectionBanner tone="success" title={formatMessage(messages.inspBannerNoDamage)}>{formatMessage(messages.inspBannerNoDamageHint)}</InspectionBanner>
            : <InspectionBanner tone="danger" title={formatMessage(messages.inspBannerDamage)}>{formatMessage(messages.inspBannerDamageHint)}</InspectionBanner>}
          {!isOwner && (
            <div className={s.actions}>
              <button type="button" className={cx(s.button, s.buttonSecondary)} onClick={() => answer(!form.hasDamage)}>
                {formatMessage(form.hasDamage ? messages.inspSwitchToNoDamage : messages.inspSwitchToDamage)}
              </button>
            </div>
          )}
          {inspection && inspection.status === 'to_validate' && <InspectionBanner tone="info" title={formatMessage(messages.inspEditIntroTitle)}>{formatMessage(messages.inspEditIntro)}</InspectionBanner>}

          {showPhotos && (
            <section className={s.card}>
              <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">1</span>{formatMessage(messages.inspPhotosDamage)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspPhotosDamageHint)}</p>
              <InspectionPhotoGrid items={series.items} max={FORM_LIMITS.photoMax} editable onAdd={series.add} onRemove={series.remove} onRetry={series.retry} title={formatMessage(messages.inspPhotosDamage)} />
              {errors.photos && <p className={s.errorText} role="alert">{formatMessage(messages[errors.photos])}</p>}
            </section>
          )}
          {form.hasDamage === true && (
            <section className={s.card}>
              <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">2</span>{formatMessage(messages.inspDamageDescriptionTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspDamageDescriptionHint)}</p>
              <textarea
                className={cx(s.textarea, errors.damageDescription && s.inputError)}
                value={form.damageDescription}
                maxLength={FORM_LIMITS.description}
                placeholder={formatMessage(messages.inspDamageDescriptionPlaceholder)}
                aria-label={formatMessage(messages.inspDamageDescriptionTitle)}
                aria-invalid={errors.damageDescription ? 'true' : undefined}
                onChange={(event) => setField('damageDescription', event.target.value)}
              />
              <p className={s.charCount}>{form.damageDescription.length}/{FORM_LIMITS.description}</p>
              {errors.damageDescription && <p className={s.errorText} role="alert">{formatMessage(messages[errors.damageDescription])}</p>}
            </section>
          )}
          <div className={s.grid2}>
            <section className={s.card}>
              <h3 className={s.cardTitle}>{formatMessage(messages.inspMileageTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspMileageHint)}</p>
              <MileageInput id="checkout-mileage" value={form.mileage} onChange={(value) => setField('mileage', value)} errorKey={errors.mileage} />
              {checkin && checkin.mileage !== null && checkin.mileage !== undefined && (
                <p className={s.cardHint}>{formatMessage(messages.inspMileageAtDeparture, { value: Number(checkin.mileage).toLocaleString() })}</p>
              )}
            </section>
            <section className={s.card}>
              <h3 className={s.cardTitle}>{formatMessage(messages.inspFuelTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspFuelHint)}</p>
              <FuelLevelSelector value={form.fuelLevel} onChange={(value) => setField('fuelLevel', value)} name="checkout-fuel" invalid={Boolean(errors.fuelLevel)} />
              {errors.fuelLevel && <p className={s.errorText} role="alert">{formatMessage(messages[errors.fuelLevel])}</p>}
              {delta !== null && delta !== 0 && <p className={s.cardHint}>{formatMessage(messages.inspFuelDelta, { count: delta })}</p>}
            </section>
          </div>
          {!isOwner && (
            <section className={s.card}>
              <div className={s.switchRow}>
                <div>
                  <h3 className={s.cardTitle}>{formatMessage(messages.inspKeysReturnTitle)}</h3>
                  <p className={s.cardHint}>{formatMessage(messages.inspKeysReturnHint)}</p>
                </div>
                <input
                  type="checkbox"
                  role="switch"
                  className={s.switchInput}
                  checked={form.keysConfirmed}
                  aria-label={formatMessage(messages.inspKeysReturnTitle)}
                  onChange={(event) => setField('keysConfirmed', event.target.checked)}
                />
              </div>
            </section>
          )}
          <section className={s.card}>
            <h3 className={s.cardTitle}>{formatMessage(messages.inspCommentOptionalTitle)}</h3>
            <p className={s.cardHint}>{formatMessage(messages.inspCommentCheckoutHint)}</p>
            <textarea
              className={s.textarea}
              value={form.comment}
              maxLength={FORM_LIMITS.comment}
              placeholder={formatMessage(messages.inspCommentPlaceholder)}
              aria-label={formatMessage(messages.inspCommentTitle)}
              onChange={(event) => setField('comment', event.target.value)}
            />
            <p className={s.charCount}>{form.comment.length}/{FORM_LIMITS.comment}</p>
          </section>
          {reviewBlock}
          <div className={s.actions}>
            {editing && (
              <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={() => { setEditing(false); seeded.current = null; reload(); setNotice(null); }}>
                {formatMessage(messages.inspCancel)}
              </button>
            )}
            <button type="button" className={s.button} disabled={busy || series.busy} onClick={submit}>{formatMessage(messages.inspValidateCheckout)}</button>
          </div>
        </form>
      )}

      {inspection && mode === 'recap' && (
        <>
          {inspection.hasDamage
            ? <InspectionBanner tone="danger" title={formatMessage(messages.inspRecapDamage)}>{formatMessage(messages.inspRecapDamageHint)}</InspectionBanner>
            : <InspectionBanner tone="success" title={formatMessage(messages.inspRecapNoDamage)} />}
          <InspectionDetails inspection={inspection} type={TYPE} max={FORM_LIMITS.photoMax} />
          {isOwner && reviewBlock}
          <div className={s.actions}>
            <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy || !inspection.canEdit} onClick={startEdit}>
              {formatMessage(isOwner ? messages.inspEditDeclareDamage : messages.inspEdit)}
            </button>
            <button type="button" className={s.button} disabled={busy} onClick={validateRecap}>
              {formatMessage(isOwner ? messages.inspValidateCheckoutRenter : messages.inspValidateCheckout)}
            </button>
          </div>
        </>
      )}

      {inspection && mode === 'readonly' && (
        <>
          <p><InspectionStatusBadge status={inspection.status} /></p>
          {inspection.status === 'to_validate' && !disputeActive && <InspectionBanner tone="info" title={formatMessage(messages.inspWaitingOtherParty)} />}
          {inspection.status === 'validated' && <InspectionBanner tone="success" title={formatMessage(messages.inspLockedBanner)}>{formatMessage(messages.inspLockedHint)}</InspectionBanner>}
          <InspectionDetails inspection={inspection} type={TYPE} max={FORM_LIMITS.photoMax} />
          {inspection.status === 'to_validate' && inspection.canEdit && !disputeExists && (
            <div className={s.actions}>
              <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={startEdit}>{formatMessage(messages.inspEdit)}</button>
            </div>
          )}
        </>
      )}

      {inspection && inspection.status !== 'draft' && (
        <DisputePanel
          reservationId={reservationId}
          dispute={dispute}
          canOpen={!dispute && role !== 'admin'}
          viewerRole={role}
          onChanged={async () => { seeded.current = null; await reload(); }}
        />
      )}
    </InspectionShell>
  );
}

CheckoutPage.propTypes = { reservationId: PropTypes.number.isRequired };

export default withStyles(s)(CheckoutPage);
